/* ============================================================================
 * <DataTable> — Reusable, configurable table for the ERP
 * Features:
 *  - Column visibility toggle (per-user, persisted in MongoDB via /api/user/preferences)
 *  - Column drag-and-drop reorder (HTML5 native DnD on the header)
 *  - Page size selector
 *  - Server-agnostic: receives an array of rows + column definitions
 *  - Mobile fallback: renders cards with expandable details (chevron/+)
 *
 * Usage:
 *   <DataTable
 *     prefKey="financial-receivable"          // unique per list
 *     columns={[ { key: 'ref', label: 'Ref.', render: r => r.ref, mandatory: false } ]}
 *     defaultVisible={['ref', 'pgto', 'name', 'amount']}
 *     defaultOrder={['color','ref','pgto','name','plan','due','amount']}
 *     rows={entries}
 *     keyField="id"
 *     emptyText="Nenhum lancamento"
 *     renderActions={(row) => <Buttons row={row} />}
 *     mobilePrimary={(row) => <Title row={row} />}   // big text on mobile card head
 *     mobileSecondary={(row) => <SubTitle row={row}/>}
 *     onRowClick={(row) => navigate(row)}
 *   />
 *
 * NOTE: This module imports axios + API from ../lib/api (existing pattern in repo).
 * ============================================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Settings as SettingsIcon, X, RotateCcw, GripVertical } from 'lucide-react';
import { API } from '../lib/api';

/* ------------------ Hook: per-user preference with localStorage fallback ----- */
export const useUserPref = (key, fallback) => {
  const [value, setValue] = useState(() => {
    try {
      const ls = JSON.parse(localStorage.getItem(`upref_${key}`) || 'null');
      return ls ?? fallback;
    } catch { return fallback; }
  });
  const saveTimer = useRef(null);

  // Load from backend on mount; fall back to local cache if offline.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/preferences/${encodeURIComponent(key)}`, { withCredentials: true });
        if (!mounted) return;
        if (data && data.value !== null && data.value !== undefined) {
          setValue(data.value);
          try { localStorage.setItem(`upref_${key}`, JSON.stringify(data.value)); } catch { /* ignore */ }
        }
      } catch { /* offline or unauthenticated — use local */ }
    })();
    return () => { mounted = false; };
  }, [key]);

  // Pending value being saved (so we can flush on unmount).
  const pendingValue = useRef(null);
  const flushSave = () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (pendingValue.current === null) return;
    const v = pendingValue.current;
    pendingValue.current = null;
    // Fire-and-forget; navigator.sendBeacon would be ideal but our endpoint expects JSON via auth cookies — axios is fine.
    axios.put(`${API}/user/preferences/${encodeURIComponent(key)}`, { value: v }, { withCredentials: true }).catch(() => { /* swallow */ });
  };
  useEffect(() => {
    return () => { flushSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Debounced save (avoids spamming when user toggles many columns)
  const update = (next) => {
    setValue(next);
    try { localStorage.setItem(`upref_${key}`, JSON.stringify(next)); } catch { /* ignore */ }
    pendingValue.current = next;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { flushSave(); }, 500);
  };

  return [value, update];
};

/* ------------------ Column config drawer ----------------------------------- */
const ColumnDrawer = ({ open, onClose, columns, visible, onToggle, order, onReorder, pageSize, setPageSize, onReset, prefKey, extraSection }) => {
  if (!open) return null;
  const ordered = order.map(k => columns.find(c => c.key === k)).filter(Boolean);
  const remaining = columns.filter(c => !order.includes(c.key));
  const all = [...ordered, ...remaining];
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} data-testid="column-drawer-overlay" />
      <aside className="fixed right-0 top-0 bottom-0 z-50 w-80 bg-white border-l border-slate-200 shadow-2xl flex flex-col" data-testid={`column-drawer-${prefKey}`}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-black text-slate-900 text-sm flex items-center gap-2"><SettingsIcon size={15} className="text-violet-600" />Mostrar e Ocultar Colunas</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="column-drawer-close"><X size={16} /></button>
        </div>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Colunas</p>
          <button onClick={onReset} className="text-xs text-violet-600 font-bold hover:text-violet-800 flex items-center gap-1" data-testid="column-drawer-reset"><RotateCcw size={12} />Redefinir</button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {all.map((col, idx) => {
            const checked = visible.includes(col.key);
            const isMandatory = col.mandatory;
            return (
              <div key={col.key} draggable={!isMandatory} onDragStart={(e) => { if (isMandatory) return; e.dataTransfer.setData('text/plain', col.key); }}
                onDragOver={(e) => { if (!isMandatory) e.preventDefault(); }}
                onDrop={(e) => { if (isMandatory) return; e.preventDefault(); const from = e.dataTransfer.getData('text/plain'); if (from && from !== col.key) onReorder(from, col.key); }}
                className={`px-4 py-2 flex items-center gap-3 hover:bg-slate-50 ${isMandatory ? '' : 'cursor-move'}`} data-testid={`col-row-${col.key}`}>
                <GripVertical size={12} className="text-slate-300 flex-shrink-0" />
                <button onClick={() => !isMandatory && onToggle(col.key)} disabled={isMandatory} data-testid={`col-toggle-${col.key}`}
                  className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-gradient-to-r from-blue-600 to-violet-600' : 'bg-slate-300'} ${isMandatory ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
                <span className={`text-sm flex-1 ${checked ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>{col.label}</span>
                <span className="text-[10px] text-slate-300">{idx + 1}</span>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
          {extraSection && (
            <div className="mb-2 pb-2 border-b border-slate-200/70" data-testid={`drawer-extra-${prefKey}`}>
              {extraSection}
            </div>
          )}
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Registros por pagina</label>
          <div className="flex gap-1">
            {[10, 25, 50, 100].map(n => (
              <button key={n} onClick={() => setPageSize(n)} data-testid={`pagesize-${n}`}
                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${pageSize === n ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <div className="px-4 py-2 border-t border-slate-200 bg-amber-50">
          <p className="text-[11px] text-amber-700 leading-tight"><strong>Dica:</strong> arraste os itens para reordenar as colunas.</p>
        </div>
      </aside>
    </>
  );
};

/* ------------------ Main DataTable ----------------------------------------- */
export const DataTable = ({
  prefKey,
  columns,
  defaultVisible,
  defaultOrder,
  defaultPageSize = 25,
  rows,
  keyField = 'id',
  emptyText = 'Nenhum registro',
  renderActions = null,
  mobilePrimary = null,
  mobileSecondary = null,
  mobileBadge = null,
  onRowClick = null,
  rowClassName = null,
  toolbarLeft = null,
  toolbarRight = null,
  loading = false,
  totalLabel = null,
  // iter77ct.91: selecao multi-linha para conferencia visual (opt-in).
  // Quando true, clique na linha alterna selecao (destaque azul forte). Nao interfere
  // com renderActions ou onRowClick externo se este for informado.
  enableRowSelection = false,
  // iter77ct.92: secao extra "disfarcada" no painel de configuracao de colunas.
  // Fica entre a lista de colunas e o seletor de "Registros por pagina".
  // Usado ex.: botao de importar lancamentos financeiros no Financeiro.
  drawerExtraSection = null,
  // iter77ct.101: callback opcional invocado quando a selecao muda.
  // Recebe array de ids selecionados. Permite o parent renderizar
  // acoes contextuais externas (ex.: "Baixar Selecionados").
  onSelectionChange = null,
  // iter77ct.101: render prop opcional para injetar botoes na barra de selecao.
  // Recebe { ids, clear, rows } - rows sao os objetos completos correspondentes.
  selectionActions = null,
}) => {
  const [prefs, savePrefs] = useUserPref(`table_${prefKey}`, { visible: defaultVisible, order: defaultOrder, pageSize: defaultPageSize });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleRowSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  // iter77ct.101: notifica o parent quando a selecao muda
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(Array.from(selectedIds));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const visible = (prefs?.visible) || defaultVisible;
  const order = (prefs?.order) || defaultOrder;
  const pageSize = (prefs?.pageSize) || defaultPageSize;

  const orderedVisibleCols = useMemo(() => {
    const cols = order.map(k => columns.find(c => c.key === k && visible.includes(k))).filter(Boolean);
    // append any visible column not in order (e.g. brand-new column)
    visible.forEach(k => { if (!order.includes(k)) { const c = columns.find(x => x.key === k); if (c) cols.push(c); } });
    return cols;
  }, [order, visible, columns]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find(c => c.key === sortKey);
    if (!col || !col.sortValue) return rows;
    const sorted = [...rows].sort((a, b) => {
      const va = col.sortValue(a);
      const vb = col.sortValue(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va).localeCompare(String(vb), 'pt-BR');
    });
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [rows, sortKey, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const toggleColumn = (k) => {
    const next = visible.includes(k) ? visible.filter(x => x !== k) : [...visible, k];
    savePrefs({ visible: next, order, pageSize });
  };
  const reorderColumn = (fromKey, toKey) => {
    const filtered = order.filter(k => k !== fromKey);
    const toIdx = filtered.indexOf(toKey);
    const next = [...filtered.slice(0, toIdx), fromKey, ...filtered.slice(toIdx)];
    savePrefs({ visible, order: next, pageSize });
  };
  const setPageSize = (n) => savePrefs({ visible, order, pageSize: n });
  const resetPrefs = () => savePrefs({ visible: defaultVisible, order: defaultOrder, pageSize: defaultPageSize });
  const toggleSort = (k) => {
    if (sortKey === k) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(k); setSortDir('asc'); }
  };

  return (
    <div className="space-y-3" data-testid={`datatable-${prefKey}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">{toolbarLeft}</div>
        <div className="flex items-center gap-2">
          {totalLabel && <span className="text-xs text-slate-500 font-medium hidden sm:inline" data-testid="datatable-total">{totalLabel}</span>}
          {toolbarRight}
          <button onClick={() => setDrawerOpen(true)} title="Configurar colunas" data-testid="datatable-config-btn"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-violet-200 bg-white hover:bg-violet-50 text-slate-500 hover:text-violet-700 transition-colors">
            <SettingsIcon size={13} />
          </button>
        </div>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`datatable-table-${prefKey}`}>
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {orderedVisibleCols.map(col => (
                  <th key={col.key}
                    draggable={!col.mandatory}
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', col.key); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); const from = e.dataTransfer.getData('text/plain'); if (from && from !== col.key) reorderColumn(from, col.key); }}
                    className={`text-left text-[11px] font-black uppercase tracking-wider text-slate-500 px-3 py-2.5 ${col.sortValue ? 'cursor-pointer hover:text-violet-700' : ''} ${col.align === 'right' ? 'text-right' : ''} ${col.headClass || ''}`}
                    onClick={() => col.sortValue && toggleSort(col.key)}
                    data-testid={`th-${col.key}`}>
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
                    </span>
                  </th>
                ))}
                {renderActions && <th className="px-3 py-2.5"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={orderedVisibleCols.length + (renderActions ? 1 : 0)} className="py-10 text-center text-slate-400 text-sm">Carregando...</td></tr>
              )}
              {!loading && pageRows.map(row => {
                const rid = row[keyField];
                const isSelected = enableRowSelection && selectedIds.has(rid);
                return (
                  <tr key={rid}
                    className={`transition-colors ${isSelected
                      ? 'bg-violet-100 hover:bg-violet-100 border-l-4 border-violet-600'
                      : `hover:bg-violet-50/40 border-l-4 ${(rowClassName && rowClassName(row)) || 'border-transparent'}`} ${(enableRowSelection || onRowClick) ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (enableRowSelection) toggleRowSelection(rid);
                      onRowClick?.(row);
                    }}
                    data-testid={`row-${rid}`}
                    data-selected={isSelected ? 'true' : 'false'}>
                    {orderedVisibleCols.map(col => (
                      <td key={col.key} className={`px-3 py-2 align-middle ${col.align === 'right' ? 'text-right' : ''} ${col.cellClass || ''}`} data-testid={`cell-${col.key}-${rid}`}>
                        {col.render(row)}
                      </td>
                    ))}
                    {renderActions && <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>{renderActions(row)}</td>}
                  </tr>
                );
              })}
              {!loading && pageRows.length === 0 && (
                <tr><td colSpan={orderedVisibleCols.length + (renderActions ? 1 : 0)} className="py-10 text-center text-slate-400 text-sm">{emptyText}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* iter77ct.91: barra de selecao (visivel apenas quando ha itens selecionados) */}
        {enableRowSelection && selectedIds.size > 0 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-violet-200 bg-violet-50 text-xs" data-testid="datatable-selection-bar">
            <span className="text-violet-800 font-semibold">
              {selectedIds.size} {selectedIds.size === 1 ? 'linha selecionada' : 'linhas selecionadas'}
            </span>
            <div className="flex items-center gap-2">
              {selectionActions && selectionActions({
                ids: Array.from(selectedIds),
                rows: rows.filter(r => selectedIds.has(r[keyField])),
                clear: clearSelection,
              })}
              <button onClick={clearSelection} className="px-2 py-1 rounded bg-white border border-violet-300 text-violet-700 hover:bg-violet-100 font-medium" data-testid="datatable-clear-selection">
                Limpar selecao
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {sortedRows.length > pageSize && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50 text-xs">
            <span className="text-slate-500">Pagina {safePage} de {totalPages} · {sortedRows.length} registros</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(1)} disabled={safePage === 1} className="px-2 py-1 rounded bg-white border border-slate-200 text-slate-600 disabled:opacity-40">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} className="px-2 py-1 rounded bg-white border border-slate-200 text-slate-600 disabled:opacity-40">‹</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="px-2 py-1 rounded bg-white border border-slate-200 text-slate-600 disabled:opacity-40">›</button>
              <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="px-2 py-1 rounded bg-white border border-slate-200 text-slate-600 disabled:opacity-40">»</button>
            </div>
          </div>
        )}
      </div>

      {/* MOBILE CARDS with expandable detail */}
      <div className="md:hidden space-y-2">
        {loading && <div className="py-10 text-center text-slate-400 text-sm">Carregando...</div>}
        {!loading && pageRows.map(row => {
          const isOpen = expandedRow === row[keyField];
          return (
            <div key={row[keyField]} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`mobile-card-${row[keyField]}`}>
              <button onClick={() => setExpandedRow(isOpen ? null : row[keyField])} className="w-full text-left px-4 py-3 flex items-center gap-3">
                {mobileBadge && <div className="flex-shrink-0">{mobileBadge(row)}</div>}
                <div className="flex-1 min-w-0">
                  {mobilePrimary && <div className="text-sm font-bold text-slate-900 truncate">{mobilePrimary(row)}</div>}
                  {mobileSecondary && <div className="text-xs text-slate-500 truncate mt-0.5">{mobileSecondary(row)}</div>}
                </div>
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-3 pt-1 border-t border-slate-100 bg-slate-50/50 space-y-1.5" data-testid={`mobile-detail-${row[keyField]}`}>
                  {orderedVisibleCols.map(col => (
                    <div key={col.key} className="flex items-start justify-between gap-3 text-xs">
                      <span className="text-slate-500 font-medium flex-shrink-0">{col.label}</span>
                      <span className="text-slate-900 text-right break-words">{col.render(row)}</span>
                    </div>
                  ))}
                  {renderActions && <div className="pt-2 flex gap-1.5 justify-end">{renderActions(row)}</div>}
                </div>
              )}
            </div>
          );
        })}
        {!loading && pageRows.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">{emptyText}</div>}
      </div>

      <ColumnDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        columns={columns}
        visible={visible}
        onToggle={toggleColumn}
        order={order}
        onReorder={reorderColumn}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onReset={resetPrefs}
        prefKey={prefKey}
        extraSection={drawerExtraSection}
      />
    </div>
  );
};

export default DataTable;
